import { useMemo } from 'react';

import { Box, Grid, Stack, Typography } from '@mui/material';
import { PricingTerm } from '@prisma/client';

import MainCard from 'src/components/MainCard/MainCard';
import { ModalBox } from 'src/components/ModalContent/Modal';
import useGeneratePresignedUrl from 'src/hooks/useGeneratePresignedUrl';
import usePresignedUrls from 'src/hooks/usePresignedUrls';
import { ProductCatalogue } from 'src/hooks/usePricings';

import ProductCard from './PricingProductCard';

export const ViewPricingModal = ({ open, handleCloseModal, offer }) => {
    const { validateAwsURL } = useGeneratePresignedUrl();
    const pricings = useMemo(() => {
        return offer?.pricings || [];
    }, [offer]);
    const products = pricings.map((pricing) => {
        return pricing.product;
    });

    const imageUrls = usePresignedUrls(products, validateAwsURL);

    return (
        <ModalBox
            open={open}
            handleCloseModal={handleCloseModal}
            title="View Offer"
            widthSize="640px"
        >
            <Stack spacing={3}>
                <Box sx={{ paddingBottom: 3, paddingTop: '5px' }}>
                    <Grid container spacing={0} sx={{ alignItems: 'stretch' }}>
                        <Grid
                            item
                            xs={9}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                            }}
                        >
                            <label className="mb-5 font-bold">Pricing offer Name</label>
                            <Box
                                sx={{
                                    padding: '10px',
                                    border: 'thin solid #F39E4E',
                                    borderRadius: '10px 0 0 10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '100%',
                                }}
                            >
                                {offer?.name}
                            </Box>
                        </Grid>
                        <Grid
                            item
                            xs={3}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                            }}
                        >
                            <label className="mb-5 font-bold">Contract Term</label>
                            <Box
                                sx={{
                                    padding: '10px',
                                    border: 'thin solid #F39E4E',
                                    borderRadius: '0 10px 10px 0',
                                    backgroundColor: '#F39E4E',
                                    color: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '100%',
                                }}
                            >
                                {offer?.term === PricingTerm.CUSTOM ? (
                                    <Typography>
                                        {`${offer?.term.split('_')[0]}`.slice(0)}
                                        {` - ${offer.customTerm} Months`}
                                    </Typography>
                                ) : (
                                    <Typography>
                                        {`${offer?.term.split('_')[0]}`.slice(1)} Months
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
                <MainCard>
                    {offer?.primaryProducts.length > 0 && (
                        <Box>
                            <h3 style={{ color: '#F18F34' }} className="m-0">
                                Primary Product
                            </h3>
                            <ProductCard
                                styling="#F18F34"
                                pricings={offer?.pricings.filter((pricing) => {
                                    return pricing.product.catalogue === ProductCatalogue.PRIMARY;
                                })}
                                imageUrls={imageUrls}
                            />
                        </Box>
                    )}
                    {offer?.secondaryProducts.length > 0 && (
                        <Box sx={{ marginTop: '30px' }}>
                            <h3 style={{ color: '#FDC800' }} className="m-0">
                                Secondary Product
                            </h3>
                            <ProductCard
                                styling="#FDC800"
                                pricings={offer?.pricings.filter((pricing) => {
                                    return pricing.product.catalogue === ProductCatalogue.SECONDARY;
                                })}
                                imageUrls={imageUrls}
                            />
                        </Box>
                    )}
                </MainCard>
                <Stack
                    spacing={2}
                    sx={{
                        border: 'thin solid #FEE380',
                        padding: '10px 20px',
                        borderRadius: '10px',
                    }}
                >
                    <Box sx={{ display: 'flex' }}>
                        <Typography sx={{ fontWeight: 'bold', marginRight: '20px' }}>
                            Sales Condition?
                        </Typography>
                        <Typography>{offer?.salesConditions}</Typography>
                    </Box>
                    {offer?.salesConditions === 'YES' && (
                        <Stack spacing={2}>
                            <Box sx={{ display: 'flex' }}>
                                <Typography sx={{ fontWeight: 'bold', marginRight: '20px' }}>
                                    GPV Conditions?
                                </Typography>
                                <Typography>{offer?.gpvConditions}</Typography>
                            </Box>
                            {offer?.gpvConditions === 'YES' && (
                                <Box sx={{ display: 'flex' }}>
                                    <Box sx={{ display: 'flex' }}>
                                        <Typography
                                            sx={{ fontWeight: 'bold', marginRight: '20px' }}
                                        >
                                            Lowest GPV
                                        </Typography>
                                        <Typography>{offer?.gpvLowest}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex' }}>
                                        <Typography
                                            sx={{
                                                fontWeight: 'bold',
                                                marginRight: '20px',
                                                marginLeft: '20px',
                                            }}
                                        >
                                            Highest GPV
                                        </Typography>
                                        <Typography>{offer?.gpvHighest}</Typography>
                                    </Box>
                                </Box>
                            )}
                            <Box sx={{ display: 'flex' }}>
                                <Typography sx={{ fontWeight: 'bold', marginRight: '20px' }}>
                                    MCC Conditions?
                                </Typography>
                                <Typography>{offer?.mccConditions}</Typography>
                            </Box>
                            {offer?.mccConditions === 'YES' && (
                                <Box>
                                    <Typography sx={{ fontWeight: 'bold', marginRight: '20px' }}>
                                        MCC
                                    </Typography>
                                    {offer?.mccs &&
                                        offer.mccs.map((mcc) => {
                                            return (
                                                <Typography>
                                                    {mcc?.code} - {mcc?.name}
                                                </Typography>
                                            );
                                        })}
                                </Box>
                            )}
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </ModalBox>
    );
};

export default ViewPricingModal;
